import {
  getAdditionalActWorkflow,
  isConsignmentAdditionalAct,
} from "./additionalActWorkflow";

const normalizeText = (value) => {
  if (value && typeof value === "object") {
    return String(value.value || value.label || value.name || "").trim();
  }

  return String(value ?? "").trim();
};

const parseAmount = (value) => {
  const raw = normalizeText(value)
    .replace(/\s+/g, "")
    .replace(/[^\d,.-]/g, "");

  if (!raw) return null;

  let normalized = raw;
  if (raw.includes(",") && raw.includes(".")) {
    normalized =
      raw.lastIndexOf(",") > raw.lastIndexOf(".")
        ? raw.replace(/\./g, "").replace(",", ".")
        : raw.replace(/,/g, "");
  } else {
    const separator = raw.includes(",") ? "," : raw.includes(".") ? "." : "";
    if (separator) {
      const parts = raw.split(separator);
      const looksLikeThousands =
        parts.length > 1 &&
        parts.slice(1).every((part) => part.length === 3) &&
        parts.every((part) => /^\d+$/.test(part));

      normalized = looksLikeThousands
        ? parts.join("")
        : `${parts.slice(0, -1).join("")}.${parts[parts.length - 1]}`;
    }
  }
  const amount = Number(normalized);

  return Number.isFinite(amount) ? amount : null;
};

export const isValidAdditionalActAmount = (value) => {
  const amount = parseAmount(value);
  return amount !== null && amount > 0;
};

const amountsMatch = (left, right) => {
  const leftAmount = parseAmount(left);
  const rightAmount = parseAmount(right);

  return (
    leftAmount !== null &&
    rightAmount !== null &&
    leftAmount > 0 &&
    rightAmount > 0 &&
    Math.abs(leftAmount - rightAmount) < 0.005
  );
};

const CI_ADDRESS_FIELDS = [
  "address_line1",
  "city",
  "state",
  "postal_code",
  "country",
];

const getCiAddress = (addresses) => {
  const ciAddresses = (Array.isArray(addresses) ? addresses : []).filter(
    (address) =>
      normalizeText(address?.categorie || address?.category).toLowerCase() ===
      "ci",
  );

  if (!ciAddresses.length) return null;

  // If older data contains more than one CI address, use the most complete one.
  return ciAddresses.reduce((bestAddress, address) => {
    const completeness = CI_ADDRESS_FIELDS.filter((field) =>
      normalizeText(address?.[field]),
    ).length;
    const bestCompleteness = CI_ADDRESS_FIELDS.filter((field) =>
      normalizeText(bestAddress?.[field]),
    ).length;

    return completeness > bestCompleteness ? address : bestAddress;
  }, ciAddresses[0]);
};

const requirement = (id, label, value, missingMessage, validator) => {
  const normalizedValue = normalizeText(value);
  const isComplete = validator
    ? validator(normalizedValue)
    : Boolean(normalizedValue);

  return {
    id,
    label,
    isComplete,
    value: isComplete ? normalizedValue : "",
    missingMessage,
  };
};

export const getAdditionalActReadiness = ({
  jiraFields,
  vendorData,
  currentPrice,
  newPrice,
  acceptedPrice,
  fallbackPrice,
  additionalActPreview,
}) => {
  const vendor = vendorData?.vendor || {};
  const ciAddress = getCiAddress(vendorData?.addresses);
  const proposedPrice = normalizeText(newPrice);
  const confirmedPrice = normalizeText(acceptedPrice || currentPrice);
  const finalPrice = confirmedPrice || normalizeText(fallbackPrice);
  const isPriceAccepted = amountsMatch(proposedPrice, confirmedPrice);

  const values = {
    Tip_Contract: normalizeText(jiraFields?.Contract),
    nume: normalizeText(jiraFields?.nume || vendor?.name),
    telefon: normalizeText(jiraFields?.telefon || vendor?.phone),
    email: normalizeText(jiraFields?.email || vendor?.email),
    NrContract: normalizeText(jiraFields?.NrContract),
    dataIntrare: normalizeText(jiraFields?.DataIntrare),
    pretFinal: isPriceAccepted ? proposedPrice : finalPrice,
    serieCI: normalizeText(vendor?.serieCI),
    nrCI: normalizeText(vendor?.nrCI),
    addressLine1: normalizeText(ciAddress?.address_line1),
    city: normalizeText(ciAddress?.city),
    state: normalizeText(ciAddress?.state),
    postalCode: normalizeText(ciAddress?.postal_code),
    country: normalizeText(ciAddress?.country),
  };

  values.adresaLinia1 = [
    values.addressLine1,
    normalizeText(ciAddress?.address_line2),
    values.city,
    values.state,
    values.postalCode,
    values.country,
  ]
    .filter(Boolean)
    .join(", ");

  const requirements = [
    {
      id: "accepted-price",
      label: "Noua sumă este acceptată",
      isComplete: isPriceAccepted,
      value: isPriceAccepted ? `${proposedPrice} RON` : "",
      missingMessage: !proposedPrice
        ? "Introdu mai întâi noua sumă propusă."
        : !isValidAdditionalActAmount(proposedPrice)
          ? "Introdu o sumă validă, mai mare decât zero."
          : "Salvează suma propusă folosind „Preț acceptat”.",
    },
    requirement(
      "contract-type",
      "Tip contract (LP001–LP006)",
      values.Tip_Contract,
      values.Tip_Contract
        ? "Tipul de contract nu are un șablon compatibil."
        : "Lipsește tipul contractului din JIRA.",
      (value) => /\bLP00[1-6]\b/i.test(value),
    ),
    requirement(
      "name",
      "Nume vânzător",
      values.nume,
      "Lipsește numele vânzătorului.",
    ),
    requirement(
      "phone",
      "Telefon",
      values.telefon,
      "Lipsește telefonul vânzătorului.",
    ),
    requirement(
      "email",
      "Email",
      values.email,
      "Lipsește emailul vânzătorului.",
    ),
    requirement(
      "contract-number",
      "Număr contract",
      values.NrContract,
      "Lipsește numărul contractului din JIRA.",
    ),
    requirement(
      "entry-date",
      "Data contractului",
      values.dataIntrare,
      "Lipsește data contractului din JIRA.",
    ),
    requirement(
      "id-series",
      "Serie CI",
      values.serieCI,
      "Lipsește seria CI din Informații furnizor.",
    ),
    requirement(
      "id-number",
      "Număr CI",
      values.nrCI,
      "Lipsește numărul CI din Informații furnizor.",
    ),
    requirement(
      "address-line",
      "Adresă CI",
      values.addressLine1,
      ciAddress
        ? "Lipsește strada din adresa CI."
        : "Lipsește adresa cu categoria CI din Informații furnizor.",
    ),
    requirement("city", "Oraș", values.city, "Lipsește orașul din adresa CI."),
    requirement(
      "county",
      "Județ",
      values.state,
      "Lipsește județul din adresa CI.",
    ),
    requirement(
      "postal-code",
      "Cod poștal",
      values.postalCode,
      "Lipsește codul poștal din adresa CI.",
    ),
    requirement(
      "country",
      "Țară",
      values.country,
      "Lipsește țara din adresa CI.",
    ),
  ];

  if (getAdditionalActWorkflow(values.Tip_Contract).provider === "zoho") {
    const isConsignment = isConsignmentAdditionalAct(values.Tip_Contract);
    values.nrAct = isConsignment ? "LP013" : "LP014";
    const preview = additionalActPreview?.data;
    const initialAmount = parseAmount(preview?.pretInitial);
    const newAmount = parseAmount(preview?.pretNou);
    const difference = parseAmount(preview?.diffPretAct);
    const isPreviewValid = Boolean(
      preview?.ok === true &&
        !additionalActPreview?.loading &&
        !additionalActPreview?.error &&
        amountsMatch(preview.pretNou, values.pretFinal),
    );
    const hasValidDecrease = Boolean(
      isPreviewValid &&
        initialAmount > newAmount &&
        newAmount > 0 &&
        difference > 0 &&
        Math.abs(initialAmount - newAmount - difference) < 0.005,
    );
    const previewMessage = additionalActPreview?.loading
      ? "Se verifică datele raportului Quality Check…"
      : additionalActPreview?.error ||
        "Verifică raportul Quality Check și prețul acceptat, apoi reîncarcă datele.";

    requirements.push(
      requirement(
        "additional-act-number",
        "Număr act adițional",
        values.nrAct,
        "",
      ),
      requirement(
        "initial-price",
        isConsignment
          ? "Preț inițial în consignație (RON)"
          : "Preț inițial de vânzare directă (RON)",
        preview?.pretInitial,
        previewMessage,
        () => isPreviewValid && initialAmount > 0,
      ),
      requirement(
        "price-decrease",
        "Diminuarea prețului (RON)",
        preview?.diffPretAct,
        isPreviewValid
          ? "Prețul nou trebuie să fie mai mic decât prețul inițial, iar diferența trebuie să fie corectă."
          : previewMessage,
        () => hasValidDecrease,
      ),
      requirement(
        "qc-report-id",
        "ID raport Quality Check",
        preview?.idQc,
        previewMessage,
        (value) => isPreviewValid && Boolean(value),
      ),
      requirement(
        "qc-report-date",
        "Data raportului Quality Check",
        preview?.dataQC,
        previewMessage,
        (value) => isPreviewValid && /^\d{2}\.\d{2}\.\d{4}$/.test(value),
      ),
    );
  }

  const completedCount = requirements.filter((item) => item.isComplete).length;

  return {
    requirements,
    completedCount,
    missingCount: requirements.length - completedCount,
    totalCount: requirements.length,
    isReady: completedCount === requirements.length,
    isPriceAccepted,
    values,
  };
};
